import { useState } from "react";
import { listAdminVisitors, updateVisitorBlacklist } from "./api";
import { DateRange, SortTh } from "./ListControls";
import { nextSort, type SortState } from "./list-sort";
import { VisitorDetailModal } from "./VisitorDetailModal";
import { usePaginatedResource } from "./use-paginated-resource";
import { EmptyRow } from "../dashboard/ui";
import { formatDateTime } from "../dashboard/format";
import { Pagination } from "../../components/Pagination";
import { SkeletonRows } from "../../components/Skeleton";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";
import { downloadCsv } from "../../lib/csv";

type BlacklistFilter = "all" | "blacklisted" | "clear";

export function VisitorsPanel() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<BlacklistFilter>("all");
  const [sort, setSort] = useState<SortState | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [detailVisitorId, setDetailVisitorId] = useState<number | null>(null);
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const isBlacklisted = filter === "all" ? undefined : filter === "blacklisted";
  const onSort = (col: string) => setSort((current) => nextSort(current, col));

  const { rows, loading, error, page, hasNext, refresh, nextPage, prevPage } = usePaginatedResource(
    (limit, offset) =>
      listAdminVisitors({
        search: search.trim() || undefined,
        is_blacklisted: isBlacklisted,
        sort_by: sort?.by,
        sort_dir: sort?.dir,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit,
        offset,
      }),
    [search, filter, sort, dateFrom, dateTo],
  );

  async function toggleBlacklist(visitorId: number, name: string, next: boolean) {
    if (next) {
      const ok = await confirm({
        title: "Blacklist visitor?",
        message: `"${name}" will be blocked from future check-ins.`,
        confirmLabel: "Blacklist",
        danger: true,
      });
      if (!ok) return;
    }

    setPendingId(visitorId);
    try {
      await updateVisitorBlacklist(visitorId, next);
      showSuccess(next ? `Blacklisted "${name}"` : `Removed "${name}" from blacklist`);
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to update visitor");
    } finally {
      setPendingId(null);
    }
  }

  function exportCsv() {
    downloadCsv(
      "visitors.csv",
      rows.map((v) => ({
        id: v.id,
        full_name: v.full_name,
        company: v.company ?? "",
        phone: v.phone ?? "",
        id_type: v.id_type ?? "",
        is_blacklisted: v.is_blacklisted,
        created_at: v.created_at ?? "",
      })),
    );
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Visitor Directory</h2>
        <span>{rows.length} rows</span>
      </header>

      <div className="filter-row">
        <input
          className="search-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, phone, or company"
          aria-label="Search visitors"
        />
        <select
          className="search-input"
          value={filter}
          onChange={(event) => setFilter(event.target.value as BlacklistFilter)}
          aria-label="Filter by blacklist status"
        >
          <option value="all">All visitors</option>
          <option value="blacklisted">Blacklisted only</option>
          <option value="clear">Not blacklisted</option>
        </select>
        <button className="refresh-btn" type="button" onClick={() => void refresh()} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <button className="refresh-btn" type="button" onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </button>
        <DateRange from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} label="Registered" />
      </div>

      {error ? <p className="error-banner">{error}</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <SortTh label="Full Name" col="full_name" sort={sort} onSort={onSort} />
              <SortTh label="Company" col="company" sort={sort} onSort={onSort} />
              <th>Phone</th>
              <th>ID Type</th>
              <SortTh label="Status" col="blacklisted" sort={sort} onSort={onSort} />
              <SortTh label="Registered" col="created_at" sort={sort} onSort={onSort} />
              <th>Details</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={4} colSpan={8} />
            ) : rows.length === 0 ? (
              <EmptyRow label="No visitors found" colSpan={8} />
            ) : (
              rows.map((visitor) => (
                <tr key={`visitor-${visitor.id}`}>
                  <td>{visitor.full_name}</td>
                  <td>{visitor.company || "-"}</td>
                  <td>{visitor.phone || "-"}</td>
                  <td>{visitor.id_type || "-"}</td>
                  <td>
                    <span className={visitor.is_blacklisted ? "status-pill status-overstay" : "status-pill status-approved"}>
                      {visitor.is_blacklisted ? "Blacklisted" : "Clear"}
                    </span>
                  </td>
                  <td>{formatDateTime(visitor.created_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="refresh-btn"
                      onClick={() => setDetailVisitorId(visitor.id)}
                    >
                      View
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={visitor.is_blacklisted ? "refresh-btn" : "refresh-btn danger-action-btn"}
                      disabled={pendingId === visitor.id}
                      onClick={() => void toggleBlacklist(visitor.id, visitor.full_name, !visitor.is_blacklisted)}
                    >
                      {pendingId === visitor.id
                        ? "Updating..."
                        : visitor.is_blacklisted
                          ? "Remove Blacklist"
                          : "Blacklist"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} hasNext={hasNext} onPrev={prevPage} onNext={nextPage} disabled={loading} />

      {detailVisitorId !== null ? (
        <VisitorDetailModal visitorId={detailVisitorId} onClose={() => setDetailVisitorId(null)} />
      ) : null}
    </article>
  );
}
